import { useState, useMemo } from 'react';
import { FileText, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useToolUsage } from '@/hooks/useToolUsage';

const WordCounter = () => {
  useToolUsage('word_counter'); // Track tool usage

  const [text, setText] = useState('');

  const stats = useMemo(() => {
    const trimmedText = text.trim();

    // Characters (with and without spaces)
    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;

    // Words
    const words = trimmedText ? trimmedText.split(/\s+/).filter(w => w.length > 0).length : 0;

    // Sentences (split by . ! ?)
    const sentences = trimmedText ? trimmedText.split(/[.!?]+/).filter(s => s.trim().length > 0).length : 0;

    // Paragraphs (split by double newlines or single newlines)
    const paragraphs = trimmedText ? trimmedText.split(/\n\s*\n|\n/).filter(p => p.trim().length > 0).length : 0;

    // Reading time (average 200 words per minute)
    const readingTimeMinutes = Math.ceil(words / 200);
    const readingTime = readingTimeMinutes < 1 ? 'Less than a minute' :
      readingTimeMinutes === 1 ? '1 minute' : `${readingTimeMinutes} minutes`;

    // Speaking time (average 150 words per minute)
    const speakingTimeMinutes = Math.ceil(words / 150);
    const speakingTime = speakingTimeMinutes < 1 ? 'Less than a minute' :
      speakingTimeMinutes === 1 ? '1 minute' : `${speakingTimeMinutes} minutes`;

    return {
      characters,
      charactersNoSpaces,
      words,
      sentences,
      paragraphs,
      readingTime,
      speakingTime,
    };
  }, [text]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <FileText className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Word Counter</h1>
          <p className="text-muted-foreground">
            Count words, characters, sentences, and more
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Text Input */}
        <div className="lg:col-span-2">
          <Card className="glass border-border/50 h-full">
            <CardHeader>
              <CardTitle>Enter Your Text</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Start typing or paste your text here..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[400px] resize-none text-base"
              />
            </CardContent>
          </Card>
        </div>

        {/* Statistics */}
        <div className="space-y-4">
          <Card className="glass border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.words}</p>
                  <p className="text-xs text-muted-foreground">Words</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.characters}</p>
                  <p className="text-xs text-muted-foreground">Characters</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.charactersNoSpaces}</p>
                  <p className="text-xs text-muted-foreground">No Spaces</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 text-center">
                  <p className="text-2xl font-bold text-primary">{stats.sentences}</p>
                  <p className="text-xs text-muted-foreground">Sentences</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 text-center col-span-2">
                  <p className="text-2xl font-bold text-primary">{stats.paragraphs}</p>
                  <p className="text-xs text-muted-foreground">Paragraphs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Time Estimates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-sm font-medium">{stats.readingTime}</p>
                <p className="text-xs text-muted-foreground">Reading time (200 wpm)</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-sm font-medium">{stats.speakingTime}</p>
                <p className="text-xs text-muted-foreground">Speaking time (150 wpm)</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WordCounter;